import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { icons, ChevronRight, Plus, Pencil, Trash2, X, LayoutGrid } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, btnCls, useConfig } from "./crud";
import { useAuth } from "../../context/AuthContext";

const label = (t) => <label className="font-mono-tech text-[10px] tracking-widest text-slate-500 block mb-1">{t}</label>;

function ModIcon({ name, size = 18 }) {
  const I = icons[name] || LayoutGrid;
  return <I size={size} strokeWidth={1.6} />;
}

function CardForm({ initial, config, onClose, onSaved }) {
  const [m, setM] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setM({ ...m, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleRole = (r) => setM({ ...m, roles: m.roles.includes(r) ? m.roles.filter((x) => x !== r) : [...m.roles, r] });
  const save = async () => {
    if (!m.name.trim() || !m.route.trim()) { toast.error("Nombre y ruta son obligatorios"); return; }
    setSaving(true);
    try {
      const { _id, created_at, updated_at, ...rest } = m;
      const data = { ...rest, order: Number(m.order) || 0 };
      if (_id) await api.put(`/admin/content/quick_access/${_id}`, { data });
      else await api.post("/admin/content/quick_access", { data });
      toast.success("Guardado"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl max-h-[85vh] overflow-auto p-6" data-testid="qa-form-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-sm">{m._id ? "Editar acceso" : "Nuevo acceso"} — Accesos Rápidos</h3>
          <button onClick={onClose} data-testid="qa-form-close"><X size={16} className="text-slate-500" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>{label("NOMBRE DEL MÓDULO")}<input className={inputCls} value={m.name} onChange={set("name")} data-testid="qa-field-name" /></div>
          <div>{label("RUTA")}<input className={inputCls} placeholder="/matriz/..." value={m.route} onChange={set("route")} data-testid="qa-field-route" /></div>
          <div>{label("CATEGORÍA")}
            <select className={inputCls} value={m.category} onChange={set("category")} data-testid="qa-field-category">
              {(config.quick_access_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>{label("ICONO (LUCIDE)")}
            <div className="flex items-center gap-2">
              <input className={inputCls} placeholder="ej: Users" value={m.icon} onChange={set("icon")} data-testid="qa-field-icon" />
              <span className="text-[#5698C7]"><ModIcon name={m.icon} /></span>
            </div>
          </div>
          <div>{label("ORDEN")}<input type="number" className={inputCls} value={m.order} onChange={set("order")} data-testid="qa-field-order" /></div>
          <div>{label("ROLES PERMITIDOS")}
            <div className="flex gap-4 pt-2">
              {(config.user_roles || []).map((r) => (
                <label key={r} className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input type="checkbox" checked={m.roles.includes(r)} onChange={() => toggleRole(r)} data-testid={`qa-role-${r}`} /> {r}
                </label>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">{label("DESCRIPCIÓN CORTA")}<input className={inputCls} value={m.description} onChange={set("description")} data-testid="qa-field-description" /></div>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            <input type="checkbox" checked={m.visible} onChange={set("visible")} data-testid="qa-field-visible" /> Visible
          </label>
        </div>
        <div className="mt-6 flex justify-end"><button onClick={save} disabled={saving} className={btnCls} data-testid="qa-save-btn">{saving ? "..." : "GUARDAR"}</button></div>
      </div>
    </div>
  );
}

export default function QuickAccess() {
  const config = useConfig();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/content/quick_access").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const visible = items.filter((i) => i.visible !== false && (!i.roles?.length || i.roles.includes(user?.role)));
    return (config?.quick_access_categories || []).map((cat) => ({ cat, mods: visible.filter((i) => i.category === cat) })).filter((g) => g.mods.length);
  }, [items, config, user]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este acceso rápido?")) return;
    await api.delete(`/admin/content/quick_access/${id}`);
    toast.success("Eliminado"); load();
  };

  const blank = () => ({ name: "", description: "", route: "/matriz/", category: (config?.quick_access_categories || [])[0] || "",
    icon: "LayoutGrid", order: items.length, visible: true, roles: ["admin", "editor"] });

  if (!config) return null;

  return (
    <div className="border border-slate-200 rounded-lg bg-white shadow-sm p-6 mb-10" data-testid="quick-access-section">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-base font-semibold text-[#0B1220]">Configuración — accesos rápidos</h2>
          <span className="font-mono-tech text-[10px] tracking-wider bg-[#5698C7]/10 text-[#4587B8] border border-[#5698C7]/30 rounded px-1.5 py-0.5">V1.0</span>
        </div>
        {user?.role === "admin" && <button onClick={() => setEditing(blank())} className={btnCls} data-testid="qa-add-btn"><Plus size={14} /> AÑADIR ACCESO</button>}
      </div>
      <p className="text-xs text-slate-500 leading-relaxed max-w-2xl mb-6">
        Navega directamente al módulo que necesites sin recorrer los menús. Cada tarjeta abre la sección completa. Todo módulo del sistema debe ser encontrable desde esta pantalla.
      </p>

      {grouped.length === 0 && <p className="text-xs text-slate-400">Sin accesos registrados.</p>}
      {grouped.map(({ cat, mods }) => (
        <div key={cat} className="mb-6 last:mb-0">
          <h3 className="font-mono-tech text-[10px] tracking-widest text-slate-500 mb-2">{cat.toUpperCase()}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {mods.map((m) => (
              <div key={m._id} onClick={() => navigate(m.route)} role="link" tabIndex={0}
                className="group border border-slate-200 rounded-lg p-4 flex items-center gap-3 cursor-pointer hover:border-[#5698C7] hover:shadow-sm transition-colors duration-200 bg-white"
                data-testid={`qa-card-${m._id}`}>
                <span className="w-10 h-10 shrink-0 rounded-lg bg-[#5698C7]/10 text-[#5698C7] flex items-center justify-center"><ModIcon name={m.icon} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-medium text-[#0B1220] truncate">{m.name}</span>
                  {m.description && <span className="block text-[11px] text-slate-400 leading-snug line-clamp-2">{m.description}</span>}
                </span>
                {user?.role === "admin" && (
                  <span className="hidden group-hover:flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setEditing({ ...m })} className="text-slate-400 hover:text-[#5698C7]" data-testid={`qa-edit-${m._id}`}><Pencil size={13} /></button>
                    <button onClick={() => remove(m._id)} className="text-slate-400 hover:text-red-600" data-testid={`qa-delete-${m._id}`}><Trash2 size={13} /></button>
                  </span>
                )}
                <ChevronRight size={15} className="text-slate-300 group-hover:text-[#5698C7] transition-colors duration-200 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      ))}
      {editing && <CardForm initial={editing} config={config} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
